/**
 * /app/$tenantSlug/settings/careers-branding — Settings → Hiring →
 * Careers branding (HR-admin). Wave K2.5.
 *
 * Three surfaces, all writing the per-tenant careers-branding bundle
 * (tenants.careers_branding JSONB):
 *  - Accent colour: a #RRGGBB hex (native colour picker + text input,
 *    kept in lockstep). Applied to the public careers list/detail pages.
 *  - About blurb: a short plain-text intro with a LIVE character count
 *    (capped at MAX_BLURB; the server re-validates). Rendered HTML-
 *    escaped on the public page (no markup / XSS).
 *  - Logo: an image upload (PNG/JPEG/WebP, ≤ 2 MB) with client-side
 *    type+size pre-validation; the server sniffs + re-caps. Replace /
 *    remove supported.
 *
 * The colour + blurb are one JSON PATCH; the logo is a separate
 * multipart upload — matching the backend's split.
 */

import { useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { toApiError } from "@/lib/api";
import { CareersLinkBar, useCareersUrl } from "@/components/hiring/careers-links";
import {
  useCareersBranding,
  useUpdateCareersBranding,
  useUploadCareersLogo,
  useDeleteCareersLogo,
} from "@/hooks/use-hiring";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/careers-branding",
)({
  component: SettingsCareersBrandingRoute,
});

// Keep in lockstep with models.CareersBrandingMaxBlurbLen (server caps
// at this; the client only pre-empts the round-trip for a friendlier UX).
const MAX_BLURB = 600;
// Client-side logo guards (the server sniffs the bytes + caps at 2 MiB —
// these are a friendly pre-check, never the security boundary).
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const LOGO_MIME = new Set(["image/png", "image/jpeg", "image/webp"]);
// A safe fallback accent for the picker when none is set (the WorkRight
// indigo — the same default the public page falls back to).
const DEFAULT_ACCENT = "#4f46e5";
// #RRGGBB — mirrors the server's models.IsValidAccentColor.
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function ColorAndBlurbCard() {
  const brandingQ = useCareersBranding();
  const updateMut = useUpdateCareersBranding();
  const b = brandingQ.data?.branding;

  const [accent, setAccent] = useState("");
  const [blurb, setBlurb] = useState("");

  // Seed local state once the query resolves (and re-seed if it changes
  // under us, e.g. after a logo op invalidates the branding query).
  useEffect(() => {
    if (b) {
      setAccent(b.accent_color ?? "");
      setBlurb(b.blurb ?? "");
    }
  }, [b]);

  const accentValid = accent === "" || HEX_RE.test(accent);
  const blurbValid = blurb.length <= MAX_BLURB;
  const dirty =
    b != null &&
    (accent !== (b.accent_color ?? "") || blurb !== (b.blurb ?? ""));

  async function onSave() {
    if (!accentValid) {
      toast.error("Accent colour must be a hex value like #4f46e5");
      return;
    }
    if (!blurbValid) {
      toast.error(`Blurb is too long (max ${MAX_BLURB} characters)`);
      return;
    }
    try {
      // Send both fields; "" clears server-side. Normalise the accent to
      // lowercase to match the stored canonical form.
      await updateMut.mutateAsync({
        accent_color: accent.trim().toLowerCase(),
        blurb: blurb.trim(),
      });
      toast.success("Careers branding saved");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  if (brandingQ.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Look &amp; Feel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Look &amp; Feel</CardTitle>
        <CardDescription>
          The accent colour and intro shown on your public careers page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="accent">Accent Colour</Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              aria-label="Pick accent colour"
              className="h-9 pointer-coarse:h-11 w-12 cursor-pointer rounded border bg-transparent p-0.5"
              value={HEX_RE.test(accent) ? accent : DEFAULT_ACCENT}
              onChange={(e) => setAccent(e.target.value)}
            />
            <Input
              id="accent"
              className="max-w-[10rem] font-mono"
              placeholder="#4f46e5"
              value={accent}
              onChange={(e) => setAccent(e.target.value)}
            />
            {accent !== "" && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAccent("")}
              >
                Clear
              </Button>
            )}
          </div>
          {!accentValid && (
            <p className="text-xs text-destructive">
              Enter a hex colour like #4f46e5 (or clear it).
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="blurb">About Your Company</Label>
            <span
              className={
                blurb.length > MAX_BLURB
                  ? "text-xs text-destructive"
                  : "text-xs text-muted-foreground"
              }
            >
              {blurb.length}/{MAX_BLURB}
            </span>
          </div>
          <Textarea
            id="blurb"
            rows={5}
            placeholder="A sentence or two about your company, shown above your open roles."
            value={blurb}
            onChange={(e) => setBlurb(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Plain text only — shown exactly as typed on the public page.
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            disabled={!dirty || updateMut.isPending || !accentValid || !blurbValid}
            onClick={onSave}
          >
            {updateMut.isPending && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            )}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function LogoCard() {
  const brandingQ = useCareersBranding();
  const uploadMut = useUploadCareersLogo();
  const deleteMut = useDeleteCareersLogo();
  const fileRef = useRef<HTMLInputElement>(null);
  const hasLogo = brandingQ.data?.branding.has_logo ?? false;
  // Local preview of the just-picked file (object URL) so HR sees what
  // they uploaded without a cross-origin authed image route. Revoked on
  // unmount / replace.
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Always reset the input so re-selecting the same file re-fires.
    e.target.value = "";
    if (!file) return;
    if (!LOGO_MIME.has(file.type)) {
      toast.error("Logo must be a PNG, JPEG or WebP image");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be 2 MB or smaller");
      return;
    }
    try {
      await uploadMut.mutateAsync(file);
      const url = URL.createObjectURL(file);
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return url;
      });
      toast.success("Logo updated");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function onRemove() {
    try {
      await deleteMut.mutateAsync();
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return null;
      });
      toast.success("Logo removed");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  const busy = uploadMut.isPending || deleteMut.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Logo</CardTitle>
        <CardDescription>
          Shown at the top of your public careers page. PNG, JPEG or WebP,
          up to 2 MB.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {brandingQ.isLoading ? (
          <Skeleton className="h-20 w-40" />
        ) : preview ? (
          <div className="flex items-center gap-4">
            <img
              src={preview}
              alt="Careers logo preview"
              className="h-16 max-w-[12rem] rounded border bg-white object-contain p-1"
            />
            <span className="text-sm text-muted-foreground">
              Logo uploaded. It now appears on your public careers page.
            </span>
          </div>
        ) : hasLogo ? (
          <p className="text-sm text-muted-foreground">
            A logo is set — it appears at the top of your public careers
            page.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            No logo set — your company name is shown instead.
          </p>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => void onPick(e)}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {uploadMut.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-1 h-4 w-4" />
            )}
            {hasLogo ? "Replace Logo" : "Upload Logo"}
          </Button>
          {hasLogo && (
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => void onRemove()}
            >
              {deleteMut.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-1 h-4 w-4" />
              )}
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SettingsCareersBrandingRoute() {
  const careersUrl = useCareersUrl(true);
  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Careers Branding</h2>
            <p className="text-muted-foreground text-sm">
              Theme your public careers page — the logo, accent colour and
              intro candidates see there. A PNG or JPEG logo also heads
              your offer letters.
            </p>
          </div>
          <CareersLinkBar careersUrl={careersUrl} />
          <ColorAndBlurbCard />
          <LogoCard />
        </div>
      </SettingsShell>
    </>
  );
}
